import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { usePageSeo } from "@/hooks/usePageSeo";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search as SearchIcon, FileText, GraduationCap, Building2 } from "lucide-react";

export default function SearchPage() {
  usePageSeo("search", { noindex: true });
  const [params, setParams] = useSearchParams();
  const initial = params.get("q") || "";
  const [term, setTerm] = useState(initial);
  const [q, setQ] = useState(initial);

  useEffect(() => { setTerm(initial); setQ(initial); }, [initial]);

  const { data, isFetching } = useQuery({
    queryKey: ["search", q],
    queryFn: async () => (await api.get(`/search`, { params: { q } })).data,
    enabled: q.length > 0,
  });

  const submit = (e) => { e.preventDefault(); setQ(term); setParams(term ? { q: term } : {}); };
  const total = data ? data.articles.length + data.education.length + data.brokers.length : 0;

  return (
    <div className="min-h-[60vh]">
      <section className="border-b border-hair bg-canvas py-14">
        <div className="container-tight max-w-3xl">
          <h1 className="font-heading text-3xl font-bold text-ink">Search</h1>
          <form onSubmit={submit} className="mt-6 flex gap-2">
            <div className="relative flex-1">
              <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-subtle" />
              <Input value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Search market analysis, education, brokers..." className="pl-9" data-testid="search-input" autoFocus />
            </div>
            <Button type="submit" className="bg-finblue hover:bg-finblueDark" data-testid="search-submit">Search</Button>
          </form>
        </div>
      </section>

      <section className="py-14">
        <div className="container-tight max-w-3xl">
          {!q && <p className="text-center text-subtle">Enter a search term above to begin.</p>}
          {q && isFetching && <p className="text-center text-subtle">Searching...</p>}
          {q && !isFetching && total === 0 && (
            <div className="rounded-xl border border-dashed border-hair bg-canvas py-16 text-center">
              <p className="font-heading text-lg font-semibold text-ink">No results for "{q}"</p>
              <p className="mt-1 text-sm text-subtle">Try different keywords or explore our <Link to="/market-analysis" className="text-finblue">market analysis</Link>.</p>
            </div>
          )}
          {q && !isFetching && total > 0 && (
            <div className="space-y-8">
              <Group title="Market Analysis" icon={FileText} items={data.articles} to={(i) => `/market-analysis/${i.slug}`} label={(i) => i.title} sub={(i) => i.category} />
              <Group title="Education" icon={GraduationCap} items={data.education} to={(i) => `/education/${i.slug}`} label={(i) => i.title} sub={(i) => i.level} />
              <Group title="Broker Partners" icon={Building2} items={data.brokers} to={(i) => `/broker-partners/${i.slug}`} label={(i) => i.name} sub={(i) => i.tagline} />
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function Group({ title, icon: Icon, items, to, label, sub }) {
  if (!items.length) return null;
  return (
    <div>
      <h2 className="mb-3 flex items-center gap-2 font-heading text-lg font-semibold text-ink"><Icon className="h-5 w-5 text-finblue" /> {title}</h2>
      <div className="space-y-2">
        {items.map((i) => (
          <Link key={i.id} to={to(i)} className="block rounded-lg border border-hair bg-white p-4 shadow-sm transition-colors hover:border-finblue/40">
            <p className="font-medium text-ink">{label(i)}</p>
            <p className="text-sm text-subtle line-clamp-1">{sub(i)}</p>
          </Link>
        ))}
      </div>
    </div>
  );
}
